import axios from "axios";

const BUNNY_API_KEY = process.env.REACT_APP_BUNNY_API_KEY || "";
const LIBRARY_ID = process.env.REACT_APP_BUNNY_LIBRARY_ID || "";
const CDN_URL = process.env.REACT_APP_BUNNY_CDN_URL || "";
const BUNNY_STREAM_BASE_URL = "https://video.bunnycdn.com";

if (!BUNNY_API_KEY || !LIBRARY_ID) {
  // eslint-disable-next-line no-console
  console.warn(
    "Missing Bunny.net API configuration. Set REACT_APP_BUNNY_API_KEY and REACT_APP_BUNNY_LIBRARY_ID in .env"
  );
}

export const bunnyApi = axios.create({
  baseURL: BUNNY_STREAM_BASE_URL,
  headers: {
    AccessKey: BUNNY_API_KEY,
    "Content-Type": "application/json",
  },
});

export const DEFAULT_VIDEO_RESOLUTIONS = "360p,480p,720p,1080p";

export const DEFAULT_MP4_RESOLUTIONS = ["240p", "360p", "480p", "720p", "1080p"];

export const collectionAPI = {
  getAll: async (page = 1, itemsPerPage = 100, search, orderBy) => {
    const params = new URLSearchParams({
      page: String(page),
      itemsPerPage: String(itemsPerPage),
    });
    if (search) params.append("search", search);
    if (orderBy) params.append("orderBy", orderBy);
    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/collections?${params.toString()}`
    );
    return response.data;
  },

  getById: async (collectionId) => {
    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/collections/${collectionId}`
    );
    return response.data;
  },

  create: async (data) => {
    const response = await bunnyApi.post(
      `/library/${LIBRARY_ID}/collections`,
      data
    );
    return response.data;
  },

  update: async (collectionId, data) => {
    const response = await bunnyApi.post(
      `/library/${LIBRARY_ID}/collections/${collectionId}`,
      data
    );
    return response.data;
  },

  delete: async (collectionId) => {
    await bunnyApi.delete(`/library/${LIBRARY_ID}/collections/${collectionId}`);
  },
};

export const videoAPI = {
  getAll: async (page = 1, itemsPerPage = 100, search, collection, orderBy) => {
    const params = new URLSearchParams({
      page: String(page),
      itemsPerPage: String(itemsPerPage),
    });
    if (search) params.append("search", search);
    if (collection) params.append("collection", collection);
    if (orderBy) params.append("orderBy", orderBy);
    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/videos?${params.toString()}`
    );
    return response.data;
  },

  getById: async (videoId) => {
    const response = await bunnyApi.get(
      `/library/${LIBRARY_ID}/videos/${videoId}`
    );
    return response.data;
  },

  create: async (data) => {
    const requestData = {
      ...data,
      enabledResolutions: data.enabledResolutions || DEFAULT_VIDEO_RESOLUTIONS,
    };
    const response = await bunnyApi.post(
      `/library/${LIBRARY_ID}/videos`,
      requestData
    );
    return response.data;
  },

  delete: async (videoId) => {
    await bunnyApi.delete(`/library/${LIBRARY_ID}/videos/${videoId}`);
  },

  getPlayerUrl: (videoId) =>
    `https://iframe.mediadelivery.net/embed/${LIBRARY_ID}/${videoId}`,

  getThumbnailUrl: (videoId) => `https://${CDN_URL}/${videoId}/thumbnail.jpg`,

  getPlaylistUrl: (videoId) => `https://${CDN_URL}/${videoId}/playlist.m3u8`,

  getMp4Url: (videoId, resolution) =>
    `https://${CDN_URL}/${videoId}/play_${resolution}.mp4`,

  getMp4UrlMap: (videoId, resolutions = DEFAULT_MP4_RESOLUTIONS) =>
    resolutions.reduce((acc, res) => {
      acc[res] = `https://${CDN_URL}/${videoId}/play_${res}.mp4`;
      return acc;
    }, {}),
};

export const getVideoStatusText = (status) => {
  const map = {
    0: "Created",
    1: "Uploaded",
    2: "Processing",
    3: "Transcoding",
    4: "Finished",
    5: "Error",
    6: "Upload Failed",
  };
  return map[status] ?? "Unknown";
};

export const getVideoStatusColorScheme = (status) => {
  if (status === 4) return "green";
  if (status === 5 || status === 6) return "red";
  if (status === 2 || status === 3) return "orange";
  if (status === 1) return "blue";
  return "gray";
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1000;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export const formatDuration = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(
      2,
      "0"
    )}`;
  }
  return `${mins}:${String(secs).padStart(2, "0")}`;
};

export { BUNNY_API_KEY, LIBRARY_ID };
